<template>
	<v-dialog :model-value="modelValue" @update:model-value="onClose" max-width="920" persistent scrollable>
		<v-card>
			<v-card-title class="d-flex align-center">
				<iconify-icon :icon="statusIcon" class="mr-2" :style="{ fontSize: '1.5rem', color: statusColor }" />
				{{ title }}
			</v-card-title>
			<v-card-text>
				<v-alert
					v-if="currentRecipeId && requireConfirm"
					type="warning"
					variant="tonal"
					density="compact"
					class="mb-3"
				>
					当前已关联配方 #{{ currentRecipeId }}；确认后将改为新关联。
				</v-alert>
				<p class="mb-3">{{ match?.message || '烘焙已结束，可进行杯测或配方相关操作。' }}</p>
				<v-alert
					v-if="!requireConfirm && match?.status === 'linked' && match.recipe_id"
					type="success"
					variant="tonal"
					density="compact"
					class="d-flex align-center flex-wrap"
				>
					<span>
						已关联配方
						<strong>#{{ match.recipe_id }}</strong>
						<span v-if="match.match_detail">（{{ match.match_detail.summary }}）</span>
					</span>
					<v-btn
						v-if="roast && match.recipe"
						size="small"
						variant="text"
						class="ml-2"
						@click="openDetail(linkedDetailRow)"
					>
						匹配详情
					</v-btn>
				</v-alert>
				<v-alert
					v-if="requireConfirm && match?.status === 'single' && match.recipe_id"
					type="info"
					variant="tonal"
					density="compact"
				>
					<div class="d-flex align-center flex-wrap">
						<span>
							推荐关联配方
							<strong>#{{ match.recipe_id }}</strong>
							<span v-if="match.recipe">
								（
								<roast-degree-badge
									v-if="match.recipe.degree"
									:degree="match.recipe.degree"
									class="d-inline-flex"
								/>
								· {{ match.recipe.beanqty }}g
								<span v-if="match.match_detail">· {{ match.match_detail.summary }}</span>
								）
							</span>
						</span>
						<v-btn
							v-if="roast"
							size="small"
							variant="text"
							class="ml-2"
							@click="openDetail(singleDetailRow)"
						>
							匹配详情
						</v-btn>
					</div>
				</v-alert>

				<div v-if="showMatchTable" class="mt-2">
					<div class="text-subtitle-2 mb-2">
						配方匹配对比
						<span v-if="matchStatsText" class="text-medium-emphasis">（{{ matchStatsText }}）</span>
					</div>
					<v-radio-group
						v-if="requireConfirm"
						v-model="selectedRecipeId"
						hide-details
						density="compact"
						class="recipe-match-radio-group"
					>
						<v-table density="compact" class="recipe-match-table">
							<thead>
								<tr>
									<th style="width: 2.5rem"></th>
									<th>配方</th>
									<th>烘焙度</th>
									<th>入豆量</th>
									<th>匹配度</th>
									<th>符合情况</th>
									<th>操作</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(row, idx) in candidateRows"
									:key="row.recipe_id"
									:class="rowClass(row)"
									@click="isRowMatched(row) && selectCandidate(row.recipe_id)"
									:style="isRowMatched(row) ? 'cursor: pointer' : ''"
								>
									<td @click.stop>
										<v-radio
											v-if="isRowMatched(row)"
											:value="normalizeId(row.recipe_id)"
											hide-details
											density="compact"
										/>
									</td>
									<td>
										<div class="d-flex align-center flex-wrap">
											<strong>#{{ row.recipe_id }}</strong>
											<v-chip
												v-if="isRecommendRow(row, idx)"
												size="x-small"
												color="primary"
												class="ml-1"
											>
												推荐
											</v-chip>
										</div>
										<div v-if="row.recipe?.machine" class="text-caption text-medium-emphasis">
											{{ row.recipe.machine }}
										</div>
									</td>
									<td>
										<roast-degree-badge v-if="row.recipe?.degree" :degree="row.recipe.degree" />
										<span v-else>—</span>
									</td>
									<td>{{ row.recipe?.beanqty }}g</td>
									<td>
										<div class="d-flex align-center">
											<v-progress-linear
												:model-value="row.match_score"
												:color="matchScoreColor(row.match_score)"
												height="8"
												rounded
												style="min-width: 4rem; max-width: 5rem"
											/>
											<span class="ml-2 text-caption">{{ row.match_score }}</span>
										</div>
									</td>
									<td>
										<v-chip
											size="x-small"
											:color="isRowMatched(row) ? matchLevelColor(row.match_level) : 'error'"
											variant="tonal"
										>
											{{ isRowMatched(row) ? row.summary : '不符合' }}
										</v-chip>
										<div
											v-if="row.violations?.length"
											class="text-caption text-medium-emphasis mt-1 violation-lines"
										>
											<div v-for="v in row.violations" :key="v.field">
												{{ v.label }}：{{ v.message }}
											</div>
										</div>
									</td>
									<td @click.stop>
										<v-btn
											v-if="roast"
											size="small"
											variant="text"
											color="primary"
											@click="openDetail(row)"
										>
											匹配详情
										</v-btn>
									</td>
								</tr>
							</tbody>
						</v-table>
					</v-radio-group>
					<v-table v-else density="compact" class="recipe-match-table">
						<thead>
							<tr>
								<th>配方</th>
								<th>烘焙度</th>
								<th>入豆量</th>
								<th>匹配度</th>
								<th>符合情况</th>
								<th>操作</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(row, idx) in candidateRows" :key="row.recipe_id" :class="rowClass(row)">
								<td>
									<div class="d-flex align-center flex-wrap">
										<strong>#{{ row.recipe_id }}</strong>
										<v-chip
											v-if="isRecommendRow(row, idx)"
											size="x-small"
											color="primary"
											class="ml-1"
										>
											推荐
										</v-chip>
									</div>
									<div v-if="row.recipe?.machine" class="text-caption text-medium-emphasis">
										{{ row.recipe.machine }}
									</div>
								</td>
								<td>
									<roast-degree-badge v-if="row.recipe?.degree" :degree="row.recipe.degree" />
									<span v-else>—</span>
								</td>
								<td>{{ row.recipe?.beanqty }}g</td>
								<td>
									<div class="d-flex align-center">
										<v-progress-linear
											:model-value="row.match_score"
											:color="matchScoreColor(row.match_score)"
											height="8"
											rounded
											style="min-width: 4rem; max-width: 5rem"
										/>
										<span class="ml-2 text-caption">{{ row.match_score }}</span>
									</div>
								</td>
								<td>
									<v-chip
										size="x-small"
										:color="isRowMatched(row) ? matchLevelColor(row.match_level) : 'error'"
										variant="tonal"
									>
										{{ isRowMatched(row) ? row.summary : '不符合' }}
									</v-chip>
									<div
										v-if="row.violations?.length"
										class="text-caption text-medium-emphasis mt-1 violation-lines"
									>
										<div v-for="v in row.violations" :key="v.field">
											{{ v.label }}：{{ v.message }}
										</div>
									</div>
								</td>
								<td @click.stop>
									<v-btn
										v-if="roast"
										size="small"
										variant="text"
										color="primary"
										@click="openDetail(row)"
									>
										匹配详情
									</v-btn>
									<v-btn
										v-if="isRowMatched(row)"
										size="small"
										color="primary"
										variant="tonal"
										class="ml-1"
										@click="$emit('link', row.recipe_id)"
									>
										关联
									</v-btn>
								</td>
							</tr>
						</tbody>
					</v-table>
				</div>

				<v-alert
					v-if="match?.status === 'none' && !showMatchTable"
					type="info"
					variant="tonal"
					density="compact"
				>
					可将本次烘焙保存为新配方，供后续自动匹配使用。
				</v-alert>
			</v-card-text>
			<v-card-actions class="flex-wrap">
				<template v-if="requireConfirm">
					<v-btn
						v-if="canConfirmLink"
						color="primary"
						variant="tonal"
						:loading="confirming"
						@click="confirmLink"
					>
						确认关联
					</v-btn>
					<v-btn variant="outlined" @click="onCancel">取消</v-btn>
				</template>
				<template v-else>
					<v-btn
						v-if="match?.status === 'none' || match?.status === 'partial'"
						color="primary"
						@click="$emit('save-recipe')"
					>
						保存为新配方
					</v-btn>
					<v-btn v-if="match?.status === 'multiple'" variant="text" @click="$emit('save-recipe')">
						另存为新配方
					</v-btn>
					<v-btn variant="text" @click="$emit('go-recipes')">配方管理</v-btn>
					<v-spacer />
					<v-btn @click="onClose(false)">关闭</v-btn>
				</template>
				<template v-if="requireConfirm && (match?.status === 'none' || match?.status === 'multiple')">
					<v-btn variant="text" @click="$emit('save-recipe')">保存为新配方</v-btn>
					<v-spacer />
				</template>
			</v-card-actions>
		</v-card>

		<recipe-match-detail-dialog
			v-model="detailDlg"
			:recipe="detailRecipe"
			:roast="roast"
			:profile="profile"
			:summary="detailSummary"
			:match-level="detailMatchLevel"
		/>
	</v-dialog>
</template>

<script>
import RecipeMatchDetailDialog from './RecipeMatchDetailDialog.vue'
import RoastDegreeBadge from './DegreeBadge.vue'
import { roastDegreeLabel } from './roastLabels'

export default {
	components: { RecipeMatchDetailDialog, RoastDegreeBadge },
	props: {
		modelValue: Boolean,
		match: { type: Object, default: null },
		roastId: { type: [Number, String], default: null },
		roast: { type: Object, default: null },
		profile: { type: Object, default: null },
		requireConfirm: { type: Boolean, default: false },
		currentRecipeId: { type: [Number, String], default: null },
		title: { type: String, default: '烘焙完成' }
	},
	emits: ['update:modelValue', 'link', 'save-recipe', 'go-recipes', 'cancel'],
	data() {
		return {
			selectedRecipeId: null,
			confirming: false,
			detailDlg: false,
			detailRecipe: null,
			detailSummary: '',
			detailMatchLevel: ''
		}
	},
	watch: {
		match: {
			handler(m) {
				this.selectedRecipeId = null
				if (!m) return
				if (m.status === 'single' && m.recipe_id) {
					this.selectedRecipeId = this.normalizeId(m.recipe_id)
				} else if (m.fitting_count > 0 && this.firstMatchedRow) {
					this.selectedRecipeId = this.normalizeId(this.firstMatchedRow.recipe_id)
				} else if (m.status === 'multiple' && this.candidateRows.length) {
					const first = this.candidateRows.find((r) => this.isRowMatched(r))
					this.selectedRecipeId = first ? this.normalizeId(first.recipe_id) : null
				}
			},
			immediate: true
		},
		modelValue(open) {
			if (!open) {
				this.selectedRecipeId = null
				this.confirming = false
				this.detailDlg = false
			}
		}
	},
	methods: {
		degreeLabel: roastDegreeLabel,
		normalizeId(id) {
			if (id == null || id === '') return null
			const n = Number(id)
			return Number.isFinite(n) ? n : id
		},
		isRowMatched(row) {
			if (!row) return false
			if (row.matched != null) return !!row.matched
			return (row.violation_count ?? 99) < 2
		},
		isRecommendRow(row, idx) {
			if (!this.isRowMatched(row)) return false
			const firstMatchedIdx = this.candidateRows.findIndex((r) => this.isRowMatched(r))
			return idx === firstMatchedIdx
		},
		rowClass(row) {
			const classes = []
			if (
				this.requireConfirm &&
				this.normalizeId(this.selectedRecipeId) === this.normalizeId(row.recipe_id) &&
				this.isRowMatched(row)
			) {
				classes.push('bg-primary-lighten-5')
			}
			if (this.requireConfirm && !this.isRowMatched(row)) {
				classes.push('text-medium-emphasis')
			}
			return classes
		},
		selectCandidate(id) {
			const nid = this.normalizeId(id)
			const row = this.candidateRows.find((r) => this.normalizeId(r.recipe_id) === nid)
			if (!row || !this.isRowMatched(row)) return
			this.selectedRecipeId = nid
		},
		openDetail(row) {
			if (!row?.recipe) return
			this.detailRecipe = row.recipe
			this.detailSummary = row.summary || ''
			this.detailMatchLevel = row.match_level || ''
			this.detailDlg = true
		},
		matchScoreColor(score) {
			if (score >= 90) return 'success'
			if (score >= 70) return 'warning'
			return 'error'
		},
		matchLevelColor(level) {
			if (level === 'perfect') return 'success'
			if (level === 'good') return 'info'
			return 'warning'
		},
		onClose(v) {
			this.$emit('update:modelValue', v)
		},
		onCancel() {
			this.$emit('cancel')
			this.onClose(false)
		},
		confirmLink() {
			if (!this.selectedRecipeId) return
			this.confirming = true
			this.$emit('link', this.selectedRecipeId)
		}
	},
	computed: {
		candidateRows() {
			if (!this.match) return []
			if (this.match.all_details?.length) {
				return this.match.all_details
			}
			if (this.match.candidate_details?.length) {
				return this.match.candidate_details
			}
			return (this.match.candidates || []).map((r) => ({
				recipe_id: r.id,
				recipe: r,
				match_score: 0,
				match_level: 'fair',
				summary: '—',
				matched: false,
				violations: []
			}))
		},
		firstMatchedRow() {
			return this.candidateRows.find((r) => this.isRowMatched(r)) || null
		},
		showMatchTable() {
			if (!this.match || !this.candidateRows.length) return false
			if (this.requireConfirm) return true
			return this.match.status === 'multiple'
		},
		matchStatsText() {
			if (this.match?.total_recipe_count == null) return ''
			const total = this.match.total_recipe_count
			const fitting = this.match.fitting_count ?? 0
			return `共 ${total} 个配方，${fitting} 个符合`
		},
		singleDetailRow() {
			if (!this.match?.recipe) return null
			return {
				recipe: this.match.recipe,
				summary: this.match.match_detail?.summary || '',
				match_level: this.match.match_detail?.match_level || ''
			}
		},
		linkedDetailRow() {
			if (!this.match?.recipe) return null
			return {
				recipe: this.match.recipe,
				summary: this.match.match_detail?.summary || '已关联',
				match_level: this.match.match_detail?.match_level || 'perfect'
			}
		},
		canConfirmLink() {
			if (!this.match || !this.selectedRecipeId) return false
			if (this.match.status !== 'single' && this.match.status !== 'multiple') {
				// 重新匹配预览可能用 fitting_count，仍允许确认
				if (!(this.requireConfirm && this.match.fitting_count > 0)) return false
			}
			const row = this.candidateRows.find(
				(r) => this.normalizeId(r.recipe_id) === this.normalizeId(this.selectedRecipeId)
			)
			return row && this.isRowMatched(row)
		},
		statusIcon() {
			const m = {
				linked: 'mdi:check-circle',
				single: 'mdi:check-circle-outline',
				multiple: 'mdi:help-circle',
				none: 'mdi:information',
				partial: 'mdi:alert'
			}
			return m[this.match?.status] || 'mdi:coffee'
		},
		statusColor() {
			const m = {
				linked: '#2e7d32',
				single: '#1976d2',
				multiple: '#ed6c02',
				none: '#0288d1',
				partial: '#ed6c02'
			}
			return m[this.match?.status] || '#666'
		}
	}
}
</script>

<style scoped>
.recipe-match-table :deep(th),
.recipe-match-table :deep(td) {
	font-size: 0.82rem;
	vertical-align: top;
}
.violation-lines {
	max-width: 16rem;
	line-height: 1.35;
}
.recipe-match-radio-group {
	margin-inline: 0;
	padding-inline: 0;
}
.recipe-match-radio-group :deep(.v-selection-control-group) {
	display: contents;
}
.recipe-match-radio-group :deep(.v-selection-control) {
	min-height: auto;
	margin: 0;
}
</style>
